import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { ArrowLeft, ArrowRight, BookOpen, BookOpenText, Check, FileText, Folder, Home, LoaderCircle, RotateCw, Search } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TextLink,
} from "@codelittinc/carbon-design-system";
import {
  MAX_QUESTION_CHARS,
  type HandbookAskResponse,
  type HandbookDocumentResponse,
  type HandbookLibraryResponse,
  type HandbookPassage,
} from "@carbon-os/server/contract";
import { ACTION } from "../components/Panels";
import { ProcessBody } from "../components/ProcessStep";
import { trackApp, trackHandbook, type HandbookEvents, type HandbookOpenVia } from "../lib/analytics";
import { ancestry, documentCounts, folderAncestors, folderBook, folderDocuments, folderPath, folderTree, searchDocuments, sortByName, useHandbook } from "../lib/handbook";
import { isAskable, questionLength, useHandbookAsk, type AskControls, type AskView } from "../lib/handbook-ask";
import { sessionFetch } from "../lib/session";
import { useSettled } from "../lib/settle";

const UNFILED = "unfiled";
type Location = string | typeof UNFILED | null;
type HandbookTab = "ask" | "browse";
type FolderVia = HandbookEvents["handbook_folder_opened"]["via"];

/** A folder as `handbook_folder_opened` names it: its id, `unfiled`, or null for the home and a folder this person can't see. */
function folderIdFor(library: HandbookLibraryResponse, location: Location): string | null {
  if (location === UNFILED) return UNFILED;
  return location && library.folders.some((folder) => folder.id === location) ? location : null;
}

// How the next document was reached, noted by the link that goes there and
// taken by the reader when it opens. Module scope, not router state: that
// survives a reload and Back, which would report a stale way in.
let nextOpen: { documentId: string; via: HandbookOpenVia; position: number | null } | null = null;

function noteOpen(documentId: string, via: HandbookOpenVia, position: number | null = null): void {
  nextOpen = { documentId: documentId.toLowerCase(), via, position };
}

/** A plain click: a modifier or another button opens a new tab, and this one stays where it is. */
function opensHere(event: React.MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/** The note for this document, once; without one it was typed, bookmarked or reloaded. */
function takeOpen(documentId: string): { via: HandbookOpenVia; position: number | null } {
  const note = nextOpen;
  nextOpen = null;
  return note && note.documentId === documentId ? { via: note.via, position: note.position } : { via: "direct", position: null };
}

/**
 * The Company handbook, in Gatekeeper's folders (carbon-gatekeeper#235). It
 * shows exactly what Gatekeeper returns: the documents this person may read
 * and the folders on the way to them, never anything else. `/handbook/:id`
 * opens one document in place; a link inside it goes to the next. Above the
 * library, a question gets Gatekeeper's answer with its sources
 * (carbon-gatekeeper#246); the answer stays while a cited document is read.
 */
/** `onRead`: a version was marked read, which Gatekeeper (1.23.0) also acknowledges in Training. */
export function Handbook({ onRead }: { onRead?: (documentId: string, readAt: string) => void } = {}) {
  const { state: handbook, retry, refresh } = useHandbook();
  const asking = useHandbookAsk();
  const params = useParams();
  // Gatekeeper's ids are lowercase; one pasted in capitals is still the same document.
  const documentId = params.documentId?.toLowerCase();
  // The open folder is in the URL (`?folder=<id>` or `?folder=unfiled`), so a
  // breadcrumb can link to it and a reload keeps it.
  const [search, setSearch] = useSearchParams();
  const requested = search.get("folder");
  const setLocation = useCallback((next: Location) => setSearch(next ? { folder: next } : {}), [setSearch]);
  // The search is in the URL too (`?q=`): Back from a result returns to it, and
  // any link to a folder, which carries no `q`, leaves it.
  const query = search.get("q") ?? "";
  const setQuery = useCallback((next: string) => setSearch((current) => {
    const params = new URLSearchParams(current);
    if (next) params.set("q", next);
    else params.delete("q");
    return params;
  }, { replace: true }), [setSearch]);
  // Opens on Ask (the tab to the right of Browse); a folder link or a document search (a breadcrumb, a
  // bookmark) opens Browse. Kept here, which stays mounted while a cited document is read, so
  // "← Handbook" comes back to the same tab; and written to `?tab=` (no history entry of its own), so
  // the browser's Back from a citation lands on the tab, and the answer, it left.
  const urlTab = search.get("tab");
  const [tab, setTabState] = useState<HandbookTab>(() =>
    urlTab === "ask" || urlTab === "browse" ? urlTab : requested || query ? "browse" : "ask");
  useEffect(() => {
    if (urlTab === "ask" || urlTab === "browse") setTabState(urlTab);
    else if (requested || query) setTabState("browse");
  }, [urlTab, requested, query]);
  const setTab = useCallback((next: HandbookTab) => {
    setTabState(next);
    setSearch((current) => {
      const params = new URLSearchParams(current);
      params.set("tab", next);
      return params;
    }, { replace: true });
  }, [setSearch]);
  // Versions read on this page, by document: they count at once, and still
  // count if the quiet re-read of the list fails.
  const [readHere, setReadHere] = useState<Record<string, number>>({});
  const markedRead = useCallback((id: string, version: number, readAt: string) => {
    setReadHere((current) => ({ ...current, [id]: Math.max(version, current[id] ?? 0) }));
    refresh();
    onRead?.(id, readAt);
  }, [refresh, onRead]);
  const ready = handbook.kind === "ready" ? handbook.library : null;
  // Built once per answer and read, so the folder tree and a book aren't redone on every render.
  const library = useMemo<HandbookLibraryResponse | null>(() => ready && {
    ...ready,
    documents: ready.documents.map((document) => {
      const read = readHere[document.id];
      if (read === undefined || (document.lastReadVersion ?? 0) >= read) return document;
      return { ...document, lastReadVersion: read, updatedSinceRead: read < document.version };
    }),
  }, [ready, readHere]);
  // `?book=<folder>` reads the folder front to back: Next goes on through every document in it and below it.
  const bookId = search.get("book")?.toLowerCase();
  const book = useMemo(() => {
    if (!library || !bookId || !documentId) return null;
    const pages = folderBook(library, bookId);
    return pages.some((document) => document.id === documentId)
      ? { folderId: bookId, name: folderPath(library.folders, bookId).at(-1) ?? "Folder", pages }
      : null;
  }, [library, bookId, documentId]);

  if (handbook.kind === "loading") {
    return <div role="status" aria-label="Loading the handbook" className="space-y-3">
      <Skeleton className="h-8 w-64 max-w-full motion-reduce:animate-none" />
      <Skeleton className="h-40 w-full motion-reduce:animate-none" />
    </div>;
  }
  if (handbook.kind === "unsupported") {
    return <EmptyState icon={<BookOpen size={28} />} title="The handbook isn't available yet" description="It will appear here once it is turned on for Carbon." />;
  }
  if (!library) {
    return <EmptyState icon={<BookOpen size={28} />} title="The handbook couldn't be loaded" description="Try again in a moment."
      action={<Button variant="outline" onClick={() => { trackApp("retry_clicked", { area: "handbook_library" }); retry(); }}><RotateCw size={14} />Try again</Button>} />;
  }
  if (documentId) return <DocumentReader key={documentId} id={documentId} library={library} book={book} onRead={markedRead} />;
  // A folder this person can't see (or a stale link) is simply the handbook's home.
  const location: Location = requested === UNFILED || library.folders.some((folder) => folder.id === requested) ? requested : null;

  const browse = <Library library={library} location={location} setLocation={setLocation} query={query} setQuery={setQuery} />;
  // With asking off, or nothing to read, the page is the library alone.
  const askShown = library.askAvailable && asking.view.kind !== "unsupported" && library.documents.length > 0;
  return <div className="space-y-6">
    {asking.view.kind === "unsupported" && <Alert variant="info">Asking the handbook isn't turned on yet. You can still search by title.</Alert>}
    {askShown
      ? <Tabs value={tab} onValueChange={(value) => {
        const next = value === "browse" ? "browse" : "ask";
        // Only a click on a tab: the URL, a folder link and the first render pick one silently.
        if (next !== tab) trackHandbook("handbook_tab_changed", { tab: next, from: tab });
        setTab(next);
      }}>
        <TabsList aria-label="Handbook">
          <TabsTrigger value="browse">Browse</TabsTrigger>
          <TabsTrigger value="ask">Ask</TabsTrigger>
        </TabsList>
        <TabsContent value="browse" className="mt-6">{browse}</TabsContent>
        <TabsContent value="ask" className="mt-6">
          <h1 className="sr-only">Ask the handbook</h1>
          <Ask asking={asking} library={library} />
        </TabsContent>
      </Tabs>
      : browse}
  </div>;
}

type LibraryDocument = HandbookLibraryResponse["documents"][number];

/** How many of the newest documents the handbook's home lists. */
const RECENT = 5;

/**
 * Finding a document, never a list of all of them: the home is a search, what
 * changed since the person read it, the top folders and the few newest
 * documents. A folder shows its subfolders and its own documents. The search
 * looks through every document, by title and by the folders it sits in.
 */
function Library({ library, location, setLocation, query, setQuery }: {
  library: HandbookLibraryResponse; location: Location; setLocation: (location: Location) => void; query: string; setQuery: (query: string) => void;
}) {
  const tree = useMemo(() => folderTree(library.folders), [library.folders]);
  const counts = useMemo(() => documentCounts(library), [library]);
  const unfiled = useMemo(() => folderDocuments(library, null), [library]);
  const needle = query.trim();
  const results = useMemo(() => (needle ? searchDocuments(library, needle) : []), [library, needle]);
  const place = useMemo(() => {
    const ancestors = ancestry(library.folders);
    return (folderId: string | null) => ancestors(folderId).map((folder) => folder.name).join(" / ") || "Unfiled";
  }, [library.folders]);
  const firstPage = useMemo(() => (location && location !== UNFILED ? folderBook(library, location)[0] : undefined), [library, location]);
  // A folder link carries no `q`, so it leaves the search too.
  const open = (next: Location, via: FolderVia) => {
    trackHandbook("handbook_folder_opened", { folder_id: folderIdFor(library, next), via });
    setLocation(next);
  };
  // Counted once the typing stops, as how many it found: never the text.
  const settle = useSettled((text) => trackHandbook("handbook_searched", {
    result_count: searchDocuments(library, text.trim()).length,
    document_count: library.documents.length,
  }));

  if (library.documents.length === 0) {
    return <EmptyState icon={<BookOpen size={28} />} title="Nothing to read yet" description="Handbook documents shared with you, or assigned to you in training, appear here." />;
  }

  // The sidebar shows the top folders, and opens out only along the way to the open one.
  const openPath = new Set(location && location !== UNFILED ? folderAncestors(library.folders, location).map((folder) => folder.id) : []);
  const sidebar = tree.filter((folder) => folder.parentId === null || openPath.has(folder.parentId));
  const subfolders = (parentId: string | null) => tree.filter((folder) => folder.parentId === parentId);

  let body: React.ReactNode;
  if (needle) {
    body = <>
      <h1 id="handbook-heading" className="text-lg font-semibold text-text-primary">Search results</h1>
      {results.length === 0
        ? <EmptyState icon={<Search size={28} />} title="No documents match" description="Try fewer words, or a folder's name." />
        : <DocumentList documents={results} place={place} via="search_result" />}
    </>;
  } else if (location === null) {
    const updated = sortByName(library.documents.filter((document) => document.updatedSinceRead), (document) => document.title);
    // Newest first, leaving out what the updates above already list.
    const recent = library.documents.filter((document) => !document.updatedSinceRead)
      .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt)).slice(0, RECENT);
    body = <>
      <h1 id="handbook-heading" className="sr-only">Handbook</h1>
      {updated.length > 0 && <Shelf title="Updated since you read them">
        <DocumentList documents={updated} place={place} via="updated_list" />
      </Shelf>}
      <Shelf title="Browse by folder">
        <FolderCards folders={subfolders(null)} counts={counts} unfiled={unfiled.length} onOpen={(next) => open(next, "card")} />
      </Shelf>
      {recent.length > 0 && <Shelf title="Recently published">
        <DocumentList documents={recent} place={place} via="recent_list" />
      </Shelf>}
    </>;
  } else {
    const here = location === UNFILED ? unfiled : folderDocuments(library, location);
    const parentId = tree.find((folder) => folder.id === location)?.parentId ?? null;
    const inside = location === UNFILED ? [] : subfolders(location);
    body = <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          {parentId && <FolderCrumbs library={library} folderId={parentId} />}
          <h1 id="handbook-heading" className="text-lg font-semibold text-text-primary">{location === UNFILED ? "Unfiled" : folderPath(library.folders, location).at(-1)}</h1>
        </div>
        {firstPage && <Button asChild>
          <Link to={`/handbook/${firstPage.id}?book=${location}`} onClick={(event) => { if (opensHere(event)) noteOpen(firstPage.id, "book_start"); }}><BookOpenText size={16} />Read this folder</Link>
        </Button>}
      </div>
      {here.length > 0 && <DocumentList documents={here} via="folder_list" />}
      {inside.length > 0 && <FolderCards folders={inside} counts={counts} unfiled={0} onOpen={(next) => open(next, "card")} />}
      {inside.length === 0 && here.length === 0 && <EmptyState icon={<FileText size={28} />} title="No documents here" />}
    </>;
  }

  return <div className="grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)]">
    <nav aria-label="Handbook folders" className="space-y-1 self-start rounded-lg border border-border-subtle p-3">
      <FolderButton label="Handbook home" icon={<Home size={15} />} active={location === null && !needle} onClick={() => open(null, "sidebar")} />
      {sidebar.length > 0 && <div className="my-2 border-t border-border-subtle" />}
      {sidebar.map((folder) => <FolderButton key={folder.id} label={folder.name} icon={<Folder size={15} />} depth={folder.depth}
        active={location === folder.id && !needle} onClick={() => open(folder.id, "sidebar")} />)}
      {unfiled.length > 0 && <FolderButton label="Unfiled" icon={<Folder size={15} />} active={location === UNFILED && !needle} onClick={() => open(UNFILED, "sidebar")} />}
    </nav>
    <section aria-labelledby="handbook-heading" className="min-w-0 space-y-6">
      <label className="relative block">
        <span className="sr-only">Search the handbook</span>
        <Search size={16} aria-hidden className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-text-faint" />
        <Input type="search" value={query} onChange={(event) => { setQuery(event.target.value); settle(event.target.value); }}
          placeholder={`Search ${library.documents.length} documents by title or folder`} className="h-11 pl-9" />
      </label>
      {body}
    </section>
  </div>;
}

function Shelf({ title, children }: { title: string; children: React.ReactNode }) {
  return <section className="space-y-3">
    <h2 className="text-sm font-medium text-text-secondary">{title}</h2>
    {children}
  </section>;
}

function FolderCards({ folders, counts, unfiled, onOpen }: {
  folders: { id: string; name: string }[]; counts: Map<string, number>; unfiled: number; onOpen: (location: Location) => void;
}) {
  const cards = [...folders.map((folder) => ({ id: folder.id, name: folder.name, count: counts.get(folder.id) ?? 0 })),
    ...(unfiled > 0 ? [{ id: UNFILED, name: "Unfiled", count: unfiled }] : [])];
  return <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
    {cards.map((card) => <li key={card.id}>
      <button type="button" onClick={() => onOpen(card.id)}
        className="flex h-full min-h-16 w-full items-center gap-3 rounded-lg border border-border-subtle px-4 py-3 text-left hover:bg-surface-overlay">
        <Folder size={18} aria-hidden className="shrink-0 text-text-muted" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-text-primary">{card.name}</span>
          <span className="block text-xs text-text-muted">{card.count === 1 ? "1 document" : `${card.count} documents`}</span>
        </span>
      </button>
    </li>)}
  </ul>;
}

/** Documents as rows; with `place`, each says where it sits, for lists that mix folders. `via` names the list. */
function DocumentList({ documents, place, via }: {
  documents: LibraryDocument[]; place?: (folderId: string | null) => string; via: "updated_list" | "recent_list" | "search_result" | "folder_list";
}) {
  return <ul className="divide-y divide-border-subtle rounded-lg border border-border-subtle">
    {documents.map((document, index) => <li key={document.id}>
      <Link to={`/handbook/${document.id}`} onClick={(event) => { if (opensHere(event)) noteOpen(document.id, via, index + 1); }}
        className="flex min-h-14 items-center gap-3 px-4 py-3 hover:bg-surface-overlay">
        <FileText size={16} aria-hidden className="shrink-0 text-text-muted" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-text-primary">{document.title}</span>
          <span className="block truncate text-xs text-text-muted">
            {place && <>{place(document.folderId)} · </>}
            Version {document.version} · {new Date(document.publishedAt).toLocaleDateString()}
          </span>
        </span>
        {document.updatedSinceRead && <Badge variant="accent">Updated</Badge>}
      </Link>
    </li>)}
  </ul>;
}

/** "in 5 minutes", "in 1 minute", or "in a minute" when it is under one or unknown. */
function inMinutes(minutes: number | null): string {
  if (!minutes) return "in a minute";
  return minutes === 1 ? "in 1 minute" : `in ${minutes} minutes`;
}

/** The short summary the live region reads; the alerts announce themselves, so they get none. */
function announcement(view: AskView): string {
  if (view.kind === "loading") return "Searching the handbook…";
  if (view.kind !== "answer") return "";
  const { response } = view;
  if (response.outcome === "answered") {
    return `Answer ready, with ${response.sources.length} ${response.sources.length === 1 ? "source" : "sources"}.`;
  }
  return response.outcome === "not_covered" ? "The handbook doesn't cover this." : "";
}

/**
 * The ask box and, once something was asked, its result. Independent of the
 * title search below: neither touches the other. Text is only ever rendered
 * as text, never as HTML.
 */
function Ask({ asking, library }: { asking: AskControls; library: HandbookLibraryResponse }) {
  const { view, text, setText, ask, clear, signIn, results } = asking;
  const input = useRef<HTMLInputElement>(null);
  const heading = useRef<HTMLSpanElement>(null);
  const area = useRef<HTMLDivElement>(null);
  const retried = useRef(false);
  // Results shown before this mounted (on the way back from a document) are not scrolled to again.
  const scrolledTo = useRef(results);
  const length = questionLength(text);
  const pending = view.kind === "loading";

  useEffect(() => {
    if (!retried.current || view.kind !== "loading") return;
    retried.current = false;
    heading.current?.focus();
  }, [view]);

  useEffect(() => {
    if (results === scrolledTo.current) return;
    scrolledTo.current = results;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    area.current?.scrollIntoView?.({ block: "start", behavior: still ? "auto" : "smooth" });
  }, [results]);

  const retry = () => {
    if (!("question" in view)) return;
    retried.current = true;
    ask(view.question, { retry: true });
  };
  const onClear = () => {
    if (view.kind !== "idle" && view.kind !== "unsupported" && view.kind !== "loading") {
      trackHandbook("handbook_answer_cleared", { outcome: view.kind === "answer" ? view.response.outcome : view.kind });
    }
    clear();
    input.current?.focus();
  };
  const tryAgain = <Button type="button" variant="outline" className={`mt-2 ${ACTION}`} onClick={retry}>Try again</Button>;

  return <div className="space-y-6">
    <Card padding="md">
      <form role="search" aria-label="Ask the handbook" className="space-y-2" onSubmit={(event) => { event.preventDefault(); ask(text); }}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <FormField label="Ask the handbook" htmlFor="handbook-ask" className="min-w-0 flex-1"
            {...(length > MAX_QUESTION_CHARS
              ? { error: `Questions can be up to ${MAX_QUESTION_CHARS} characters. This one is ${length}.` }
              : { hint: length >= 400 ? `${length} of ${MAX_QUESTION_CHARS} characters.` : `Up to ${MAX_QUESTION_CHARS} characters.` })}>
            <Input ref={input} id="handbook-ask" type="text" autoComplete="off" value={text} onChange={(event) => setText(event.target.value)}
              placeholder="e.g. How many PTO days carry over to next year?" />
          </FormField>
          {/* Beside the input from sm up: down by the label (text-xs, 16px) and FormField's space-y-1 (4px),
              and the Input's own h-8, so the two line up. Stacked on mobile, it keeps the full touch height. */}
          <Button type="submit" className="h-11 w-full sm:mt-5 sm:h-8 sm:w-auto" disabled={pending || !isAskable(text)}>
            {pending ? <><LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />Asking…</> : "Ask"}
          </Button>
        </div>
        <p className="text-xs text-text-muted">Answers come only from handbook documents you can open, and link to them. Check the source before you act on it.</p>
      </form>
    </Card>
    <p role="status" aria-live="polite" className="sr-only">{announcement(view)}</p>
    {"question" in view && <div ref={area} className="scroll-mt-4">
      <Card padding="md">
        <CardHeader as="h2" className="mb-2"
          title={<span ref={heading} tabIndex={-1} className="outline-none">
            {view.kind === "answer" && view.response.outcome === "not_covered" ? "The handbook doesn't cover this" : "Answer"}
          </span>}
          actions={pending ? undefined : <Button type="button" variant="ghost" size="sm" onClick={onClear}>Clear</Button>} />
        <p className="break-words text-sm text-text-secondary">You asked: "{view.question}"</p>
        <div className="mt-4 space-y-4">
          {view.kind === "loading" && <div className="space-y-2">
            <Skeleton className="h-4 w-full motion-reduce:animate-none" />
            <Skeleton className="h-4 w-11/12 motion-reduce:animate-none" />
            <Skeleton className="h-4 w-2/3 motion-reduce:animate-none" />
          </div>}
          {view.kind === "answer" && <AskAnswer response={view.response} library={library} tryAgain={tryAgain} />}
          {view.kind === "rate_limited" && <Alert variant="warning" title={`You've asked a lot of questions. Try again ${inMinutes(view.minutes)}.`}>
            You can still search by title.
          </Alert>}
          {view.kind === "error" && <Alert variant="error" title="We couldn't answer right now"><p>Try again in a moment.</p>{tryAgain}</Alert>}
          {view.kind === "unavailable" && <Alert variant="warning" title="Asking the handbook is unavailable right now">
            <p>You can still search by title below.</p>{tryAgain}
          </Alert>}
          {view.kind === "needs_signin" && <Alert variant="warning" title="Sign in again to ask your question">
            <p>Your question will still be here.</p>
            <Button type="button" variant="outline" className={`mt-2 ${ACTION}`}
              onClick={() => { trackApp("signin_again_clicked", { area: "handbook_ask" }); signIn(); }}>Sign in again</Button>
          </Alert>}
          {view.kind === "signin_failed" && <Alert variant="warning">We couldn't confirm your session.</Alert>}
        </div>
      </Card>
    </div>}
  </div>;
}

/** Gatekeeper's answer as it came: prose with its citations, or passages that are not an answer. */
function AskAnswer({ response, library, tryAgain }: { response: HandbookAskResponse; library: HandbookLibraryResponse; tryAgain: React.ReactNode }) {
  if (response.outcome === "answered" && response.answer) {
    const byNumber = new Map(response.sources.map((source) => [source.number, source]));
    return <>
      <p className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed text-text-primary">
        {response.answer.sentences.map((sentence, index) => <Fragment key={index}>
          {index > 0 && " "}{sentence.text}
          {sentence.citations.map((number) => {
            const source = byNumber.get(number);
            return source && <TextLink key={number} asChild className="ml-0.5">
              <Link to={`/handbook/${source.documentId}`} aria-label={`Source ${number}: ${source.title}, version ${source.version}`}
                onClick={(event) => {
                  if (!opensHere(event)) return;
                  trackHandbook("handbook_citation_opened", { document_id: source.documentId, position: number });
                  noteOpen(source.documentId, "citation", number);
                }}>[{number}]</Link>
            </TextLink>;
          })}
        </Fragment>)}
      </p>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-text-primary">Sources</h3>
        <ol className="space-y-4">
          {response.sources.map((source) => <PassageItem key={source.number} passage={source} number={source.number} library={library} />)}
        </ol>
      </section>
    </>;
  }
  const kind = response.outcome === "not_covered" ? "related" : "matching";
  const passages = response.related.length > 0 && <ul className="space-y-4">
    {response.related.map((passage, index) => <PassageItem key={index} passage={passage} related={{ kind, position: index + 1 }} library={library} />)}
  </ul>;
  if (response.outcome === "not_covered") {
    return <>
      <p className="text-sm text-text-secondary">We couldn't find a handbook document you can open that answers this. Try other words, browse the folders below, or ask HR or IT.</p>
      {passages && <section className="space-y-3">
        <h3 className="text-sm font-semibold text-text-primary">Related passages</h3>
        <p className="text-xs text-text-muted">These may be related, but they don't answer your question.</p>
        {passages}
      </section>}
    </>;
  }
  return <>
    <Alert variant="warning" title="We couldn't write an answer right now">
      <p>These passages matched your question. Open a document to read the policy.</p>{tryAgain}
    </Alert>
    {passages && <section className="space-y-3">
      <h3 className="text-sm font-semibold text-text-primary">Matching passages</h3>
      {passages}
    </section>}
  </>;
}

/**
 * One passage: its document as a link, its version and folder, and the passage
 * itself in full. Numbered only when it is a cited source; only those count as
 * a citation opened, and the rest as a passage opened.
 */
function PassageItem({ passage, number, related, library }: {
  passage: HandbookPassage; number?: number; related?: Omit<HandbookEvents["handbook_passage_opened"], "document_id">; library: HandbookLibraryResponse;
}) {
  const listed = library.documents.find((document) => document.id === passage.documentId);
  const onOpen = (event: React.MouseEvent) => {
    if (!opensHere(event)) return;
    if (number !== undefined) {
      trackHandbook("handbook_citation_opened", { document_id: passage.documentId, position: number });
      noteOpen(passage.documentId, "citation", number);
    } else if (related) {
      trackHandbook("handbook_passage_opened", { document_id: passage.documentId, ...related });
      noteOpen(passage.documentId, "related_passage", related.position);
    }
  };
  // A document the loaded library doesn't have (it changed since) still shows, without a folder.
  const where = listed ? folderPath(library.folders, listed.folderId).join(" / ") || "Unfiled" : null;
  return <li className="space-y-1">
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {number !== undefined && <span aria-hidden className="text-sm text-text-muted">{number}.</span>}
      <TextLink asChild className="text-sm font-medium break-words">
        <Link to={`/handbook/${passage.documentId}`} onClick={onOpen}>
          {passage.title}
        </Link>
      </TextLink>
      <Badge variant="default">Version {passage.version}</Badge>
    </div>
    {where && <p className="text-xs text-text-muted">{where}</p>}
    <blockquote className="border-l-2 border-border-subtle pl-3 text-sm whitespace-pre-wrap text-text-secondary">{passage.text}</blockquote>
  </li>;
}

/** A document's or folder's place, each folder a link that opens it in the library. */
function FolderCrumbs({ library, folderId }: { library: HandbookLibraryResponse; folderId: string | null }) {
  const crumbs = folderId === null ? [{ id: UNFILED, name: "Unfiled" }] : folderAncestors(library.folders, folderId);
  return <nav aria-label="Folder" className="flex flex-wrap items-center gap-1 text-xs text-text-muted">
    {crumbs.map((crumb, index) => <span key={crumb.id} className="inline-flex items-center gap-1">
      {index > 0 && <span aria-hidden>/</span>}
      <Link to={`/handbook?folder=${crumb.id}`} className="hover:text-text-primary hover:underline"
        onClick={(event) => { if (opensHere(event)) trackHandbook("handbook_folder_opened", { folder_id: folderIdFor(library, crumb.id), via: "breadcrumb" }); }}>{crumb.name}</Link>
    </span>)}
  </nav>;
}

function FolderButton({ label, icon, active, depth = 0, onClick }: { label: string; icon: React.ReactNode; active: boolean; depth?: number; onClick: () => void }) {
  return <Button variant={active ? "outline" : "ghost"} aria-current={active ? "true" : undefined} className="w-full justify-start truncate"
    style={{ paddingLeft: `${12 + depth * 16}px` }} onClick={onClick}>{icon}<span className="truncate">{label}</span></Button>;
}

/**
 * Previous and Next for a page of the folder being read, as links, so either
 * opens in a new tab: a compact row with the position above the document, the
 * full titles below it. The arrow keys turn the page too (the one below
 * listens), but only when nothing else has the focus: never from a field, a
 * player, a scrolling table or any control that handles the key itself.
 */
function BookPager({ book, id, compact = false }: { book: Book; id: string; compact?: boolean }) {
  const navigate = useNavigate();
  const index = book.pages.findIndex((page) => page.id === id);
  const previous = book.pages[index - 1], next = book.pages[index + 1];
  const href = useCallback((page: LibraryDocument) => `/handbook/${page.id}?book=${book.folderId}`, [book.folderId]);
  const finished = (event: React.MouseEvent) => {
    if (opensHere(event)) trackHandbook("handbook_book_finished", { folder_id: book.folderId, page_count: book.pages.length });
  };
  useEffect(() => {
    if (compact) return;
    // Clicking into a wide table leaves the focus on the page, so where the
    // pointer last went down says whether an arrow means "scroll this".
    let pressed: Element | null = null;
    const press = (event: PointerEvent) => { pressed = event.target instanceof Element ? event.target : null; };
    const turn = (event: KeyboardEvent) => {
      // A held key repeats; one press is one page, never a run through the book.
      if (event.repeat || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target && target !== document.body && !target.closest("[data-book-pager]")) return;
      if (scrollsSideways(pressed)) return;
      const page = event.key === "ArrowRight" ? next : event.key === "ArrowLeft" ? previous : undefined;
      if (!page) return;
      event.preventDefault();
      noteOpen(page.id, page === next ? "book_next" : "book_previous");
      navigate(href(page));
    };
    window.addEventListener("pointerdown", press, true);
    window.addEventListener("keydown", turn);
    return () => {
      window.removeEventListener("pointerdown", press, true);
      window.removeEventListener("keydown", turn);
    };
  }, [compact, navigate, href, next, previous]);

  if (compact) {
    return <nav data-book-pager aria-label="Reading this folder" className="flex flex-wrap items-center gap-2">
      <span className="min-w-0 flex-1 text-xs text-text-muted">
        Reading <span className="font-medium text-text-secondary">{book.name}</span> · {index + 1} of {book.pages.length}
      </span>
      {previous && <Button asChild variant="outline" size="sm"><Link to={href(previous)} aria-label={`Previous: ${previous.title}`} onClick={(event) => { if (opensHere(event)) noteOpen(previous.id, "book_previous"); }}><ArrowLeft size={14} />Previous</Link></Button>}
      {next
        ? <Button asChild size="sm"><Link to={href(next)} aria-label={`Next: ${next.title}`} onClick={(event) => { if (opensHere(event)) noteOpen(next.id, "book_next"); }}>Next<ArrowRight size={14} /></Link></Button>
        : <Button asChild variant="outline" size="sm"><Link to={`/handbook?folder=${book.folderId}`} onClick={finished}><Check size={14} />Finish</Link></Button>}
    </nav>;
  }

  return <nav data-book-pager aria-label="Turn the page" className="grid grid-cols-2 gap-3">
    {previous
      ? <Button asChild variant="outline" className="h-auto min-h-14 justify-start py-3 text-left">
        <Link to={href(previous)} onClick={(event) => { if (opensHere(event)) noteOpen(previous.id, "book_previous"); }}>
          <ArrowLeft size={16} className="shrink-0" /><span className="min-w-0"><span className="block text-xs text-text-muted">Previous</span><span className="block truncate">{previous.title}</span></span>
        </Link>
      </Button>
      : <span />}
    {next
      ? <Button asChild className="h-auto min-h-14 justify-end py-3 text-right">
        <Link to={href(next)} onClick={(event) => { if (opensHere(event)) noteOpen(next.id, "book_next"); }}>
          <span className="min-w-0"><span className="block text-xs opacity-80">Next · {index + 2} of {book.pages.length}</span><span className="block truncate">{next.title}</span></span><ArrowRight size={16} className="shrink-0" />
        </Link>
      </Button>
      : <Button asChild variant="outline" className="h-auto min-h-14 justify-end py-3">
        <Link to={`/handbook?folder=${book.folderId}`} onClick={finished}><Check size={16} />Finish {book.name}</Link>
      </Button>}
  </nav>;
}

/** Whether the element, or one around it, scrolls sideways: arrows belong to it there. */
function scrollsSideways(element: Element | null): boolean {
  for (let current = element; current && current !== document.body; current = current.parentElement) {
    const overflow = getComputedStyle(current).overflowX;
    if ((overflow === "auto" || overflow === "scroll") && current.scrollWidth > current.clientWidth) return true;
  }
  return false;
}

type ReadFailure = "not_available" | "error";
type ReaderState =
  | { kind: "loading" }
  | { kind: "ready"; document: HandbookDocumentResponse }
  | { kind: "failed"; message: string; outcome: ReadFailure };

/** A load that failed: its words for the page, and whether the document is not available to this person. */
class ReadError extends Error {
  readonly outcome: ReadFailure;
  constructor(message: string, outcome: ReadFailure = "error") {
    super(message);
    this.outcome = outcome;
  }
}

/** Exported for its refusal wording only. */
export { readDocument as readDocumentForTest };

async function readDocument(id: string, since: number | null): Promise<HandbookDocumentResponse> {
  const query = since === null ? "" : `?since=${since}`;
  const res = await sessionFetch(`/api/handbook/documents/${encodeURIComponent(id)}${query}`, { cache: "no-store", headers: { Accept: "application/json" } });
  const body = (await res.json().catch(() => null)) as (HandbookDocumentResponse & { code?: string }) | null;
  if (res.status === 404 && body?.code === "unknown_version") throw new ReadError(`You haven't read version ${since}, so it can't be compared.`);
  if (res.status === 404) throw new ReadError("This document isn't available. It may have been moved or removed, or not shared with you.", "not_available");
  if (!res.ok) throw new ReadError("We couldn't load this document. Try again.");
  if (!body || body.processId !== id || !Array.isArray(body.seenVersions)) throw new ReadError("We couldn't load this document. Try again.");
  return body;
}

/**
 * One document: always the whole current version. When it changed since the
 * person last read it, a note above says what changed, with the summaries and
 * a switch that highlights the changes in the document. Opening it records
 * nothing: "I've read this version" does, and only then does the Updated badge
 * go, and the changes with it: a version marked read is never compared again.
 * A read also acknowledges the version in Training, so a process is marked once.
 */
type Book = { folderId: string; name: string; pages: LibraryDocument[] };

function DocumentReader({ id, library, book, onRead }: {
  id: string; library: HandbookLibraryResponse; book: Book | null; onRead: (id: string, version: number, readAt: string) => void;
}) {
  const navigate = useNavigate();
  const [state, setState] = useState<ReaderState>({ kind: "loading" });
  const [since, setSince] = useState<number | null>(null);
  const [highlight, setHighlight] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Bumped to load again with the same `since`.
  const [reload, setReload] = useState(0);
  // Only the newest load may set the state: a slower, older answer is dropped.
  const latest = useRef(0);
  const listed = library.documents.find((document) => document.id === id);
  // The opening is counted when the first load answers, and again after Try
  // again, with the same way in; a compare switch or a reload after a read is not.
  // The way in is taken on mount, before a quick Next could note the page after.
  const countOpen = useRef(true);
  const opened = useRef<{ via: HandbookOpenVia; position: number | null } | null>(null);
  useEffect(() => {
    opened.current ??= takeOpen(id);
  }, [id]);

  const load = useCallback(async (from: number | null) => {
    const request = ++latest.current;
    setState({ kind: "loading" });
    try {
      const document = await readDocument(id, from);
      if (request === latest.current) setState({ kind: "ready", document });
    } catch (caught) {
      if (request === latest.current) {
        setState({
          kind: "failed",
          message: caught instanceof Error ? caught.message : "We couldn't load this document.",
          outcome: caught instanceof ReadError ? caught.outcome : "error",
        });
      }
    }
  }, [id]);

  useEffect(() => {
    if (!countOpen.current || state.kind === "loading") return;
    countOpen.current = false;
    const page = book ? book.pages.findIndex((entry) => entry.id === id) : -1;
    trackHandbook("handbook_document_opened", {
      document_id: id,
      ...(opened.current ?? { via: "direct", position: null }),
      in_book: book !== null,
      book_position: page >= 0 ? page + 1 : null,
      book_page_count: book ? book.pages.length : null,
      updated_since_read: listed?.updatedSinceRead ?? false,
      outcome: state.kind === "ready" ? "shown" : state.outcome,
    });
  }, [state, id, book, listed]);

  useEffect(() => { void load(since); }, [load, since, reload]);
  // Each page of a book starts at its top.
  useEffect(() => { window.scrollTo?.({ top: 0 }); }, [id]);

  async function markRead(document: HandbookDocumentResponse) {
    setSaving(true);
    setSaveError(null);
    const res = await sessionFetch(`/api/handbook/documents/${encodeURIComponent(id)}/reads`, {
      method: "PUT", credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ versionId: document.versionId }),
    }).catch(() => null);
    setSaving(false);
    const lastRead = listed?.lastReadVersion ?? 0;
    const marked = (outcome: HandbookEvents["handbook_document_marked_read"]["outcome"]) => trackHandbook("handbook_document_marked_read", {
      document_id: id, version: document.version, reread: lastRead > 0 && lastRead < document.version, in_book: book !== null, outcome,
    });
    if (res?.ok) {
      marked("saved");
      const saved = (await res.json().catch(() => null)) as { readAt?: unknown } | null;
      // Read: back to the plain current document, from its top.
      setHighlight(false);
      setSince(null);
      onRead(id, document.version, typeof saved?.readAt === "string" ? saved.readAt : new Date().toISOString());
      // In a book the reader is at the foot, by Next: leave them there.
      if (!book) window.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }
    const body = (await res?.json().catch(() => null)) as { code?: string } | null;
    marked(body?.code === "unknown_version" ? "newer_version" : "error");
    if (body?.code === "unknown_version") {
      setSaveError("A newer version was published while you were reading. It's loaded below.");
      setSince(null);
      setReload((count) => count + 1);
    } else setSaveError("We couldn't save that. Try again.");
  }

  const readThis = state.kind === "ready" && listed?.lastReadVersion === state.document.version;

  return <article className="mx-auto max-w-4xl space-y-6">
    <div className="space-y-2">
      {book
        ? <Button variant="ghost" size="sm" onClick={() => {
          trackHandbook("handbook_folder_opened", { folder_id: folderIdFor(library, book.folderId), via: "back" });
          navigate(`/handbook?folder=${book.folderId}`);
        }}><ArrowLeft size={16} />{book.name}</Button>
        : <Button variant="ghost" size="sm" onClick={() => {
          trackHandbook("handbook_folder_opened", { folder_id: null, via: "back" });
          navigate("/handbook");
        }}><ArrowLeft size={16} />Handbook</Button>}
      {book && <BookPager book={book} id={id} compact />}
      {/* A folder the list doesn't have is Unfiled, as it is in the lists. */}
      {listed && <FolderCrumbs library={library}
        folderId={listed.folderId && library.folders.some((folder) => folder.id === listed.folderId) ? listed.folderId : null} />}
      <h1 className="text-2xl font-semibold text-text-primary">{state.kind === "ready" ? state.document.title : listed?.title ?? "Document"}</h1>
    </div>
    {state.kind === "loading" && <p role="status" className="text-sm text-text-muted"><LoaderCircle className="mr-2 inline size-4 animate-spin motion-reduce:animate-none" />Loading document…</p>}
    {state.kind === "failed" && <div className="space-y-3"><p role="alert" className="text-sm text-text-muted">{state.message}</p><Button variant="outline" onClick={() => {
      trackApp("retry_clicked", { area: "handbook_document" });
      countOpen.current = true;
      setSince(null);
      setReload((count) => count + 1);
    }}><RotateCw size={14} />Try again</Button></div>}
    {state.kind === "ready" && <>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="default">Version {state.document.version}</Badge>
        {readThis
          ? <span className="inline-flex items-center gap-1 text-xs text-text-muted"><Check size={14} aria-hidden />You've read this version</span>
          : listed?.updatedSinceRead && <Badge variant="accent">Updated since you read version {listed.lastReadVersion}</Badge>}
        {/* Only before this version is read, and only when there is more than one earlier read to choose from. Once read, nothing is compared. */}
        {!readThis && state.document.seenVersions.length > 1 && <Select value={since === null ? "latest" : String(since)} onValueChange={(value) => setSince(value === "latest" ? null : Number(value))}>
          <SelectTrigger aria-label="Compare with an earlier version you read" className="h-8 w-auto"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="latest">Changes since you last read it</SelectItem>
            {state.document.seenVersions.map((version) => <SelectItem key={version} value={String(version)}>Compare with version {version}</SelectItem>)}
          </SelectContent>
        </Select>}
      </div>

      {!readThis && state.document.previousVersion !== null && <section aria-labelledby="changes-title" className="space-y-3 rounded-lg border border-border bg-surface p-5">
        <h2 id="changes-title" className="font-medium text-text-primary">What changed since version {state.document.previousVersion}</h2>
        {/* Every version since, so one published without a summary still says it changed. */}
        <ul className="list-disc space-y-1 pl-5 text-sm text-text-secondary">
          {state.document.changeSummaries.map((change) => <li key={change.version}>
            <span className="font-medium">Version {change.version}:</span>{" "}
            {change.summary ?? <span className="text-text-muted italic">No explanation was given for this change.</span>}
          </li>)}
        </ul>
        {state.document.changes && <label className="flex items-center gap-2 text-sm text-text-secondary">
          <Switch checked={highlight} onCheckedChange={setHighlight} />Highlight the changes in the document
        </label>}
      </section>}

      <section aria-label="Document" className="rounded-lg border border-border-subtle p-5 sm:p-8">
        {highlight && !readThis && state.document.changes
          ? <p className="whitespace-pre-wrap text-sm leading-relaxed text-text-secondary">{state.document.changes.map((part, index) =>
            // The design system's soft tints behind its text colours; underline and strike-through say it without colour.
            part.type === "insert" ? <ins key={index} className="rounded-sm bg-success-soft px-0.5 text-success-text underline decoration-1 underline-offset-2">{part.text}</ins>
              : part.type === "delete" ? <del key={index} className="rounded-sm bg-error-soft px-0.5 text-error-text decoration-1">{part.text}</del>
                : <span key={index}>{part.text}</span>)}</p>
          : <ProcessBody html={state.document.bodyHtml} onLink={(target) => { noteOpen(target, "document_link"); navigate(`/handbook/${target}`); }} />}
      </section>

      {saveError && <p role="alert" className="text-sm text-error-text">{saveError}</p>}
      {!readThis && <div className="flex justify-end border-t border-border-subtle pt-5">
        <Button variant={book ? "outline" : "default"} className="w-full sm:w-auto" disabled={saving} onClick={() => void markRead(state.document)}>
          {saving ? <><LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" />Saving…</> : <><Check size={14} />I've read this version</>}
        </Button>
      </div>}
    </>}
    {book && <BookPager book={book} id={id} />}
  </article>;
}
